/**
 * SSE frame protocol for the notifications stream - pure, React-free.
 *
 * Invalidation-first contract: the client never applies frame payloads to
 * state. Every frame (connected included) means "server state may have
 * changed - refetch over REST". Payloads stay on the wire so a surgical
 * cache-update optimization remains possible without a protocol change.
 */
import type { NotificationOut } from 'connectors/notifications/types.gen';

/** Wire format of one SSE `data:` payload. Producer: notifications/src/pubsub/publisher.py */
export type SSEFrame =
  | { event: 'connected' }
  // The publisher omits server-computed fields like expired; the invalidation client never reads payloads anyway.
  | { event: 'created' | 'updated'; notification: Omit<NotificationOut, 'expired'> }
  | { event: 'resolved'; notification: { id: string } }
  | { event: 'read_state_changed'; notification: { id: string; read_at: string | null } }
  | { event: 'all_read'; read_at: string };

/**
 * Parse one SSE message body. Returns null for malformed payloads.
 *
 * A malformed frame can only mean a backend serialization bug - log it instead
 * of swallowing it silently.
 */
export function parseFrame(data: string): SSEFrame | null {
  try {
    return JSON.parse(data) as SSEFrame;
  } catch {
    console.warn('[notifications] malformed SSE frame:', data.slice(0, 200));
    return null;
  }
}
